/**
 * SharedNav — single-row simplified header (Option B).
 *
 * Used on every page (homepage, auth, dashboards, admin, booking, etc.).
 *
 * RATIONALE (this revision):
 *   The header is no longer a conversion surface. Both the "Book a cleanup"
 *   and "Become a PooPartner" buttons have been removed from the header AND
 *   the mobile drawer. Their canonical placement is now the Landing hero,
 *   where they're bumped to a larger size and act as the only primary CTAs.
 *   The nav is now a single horizontal row (Row 1 + Row 2 collapsed).
 *
 * DESKTOP (≥ md):
 *   Single row: [logo lockup]   About   Pricing   Map   Social   ........   Log in
 *
 * MOBILE (< md):
 *   Single row: [logo lockup]  ..........................  [☰ hamburger]
 *   Hamburger opens a right-side Sheet drawer (shadcn/Radix dialog):
 *     - logo lockup
 *     - center nav links (full-width tap targets)
 *     - Log in (text link)
 *   NO buttons in the drawer — Sheet handles focus trap, ESC, backdrop
 *   click, body-scroll-lock natively via Radix dialog.
 *
 * LOGO LOCKUP:
 *   - Dog mark PNG at `/brand/poopluckers-dogmark.png` (official
 *     `final_symbol.png` extracted from the brand book ZIP).
 *   - Wordmark is typed HTML (Manrope 800), so it stays crisp at any DPR
 *     and the slate part inverts with the theme automatically.
 *
 * API:
 *   <SharedNav />                            // default UNAUTH cluster (Log in)
 *   <SharedNav rightSlot={<UserMenu />} />   // injected cluster (AUTH/ADMIN)
 *
 * On UserMenu surfaces, the right side is the injected slot; the center
 * nav links still render. The mobile drawer still surfaces nav links + Log in.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import MobileNavDrawer from "@/components/MobileNavDrawer";
import { useAuth, pathForView } from "@/context/AuthContext";
import { useTheme } from "@/theme/ThemeProvider";
import ThemeToggle from "@/components/ThemeToggle";
import SharedNavSocialMenu from "@/components/SharedNavSocialMenu";

// DO NOT inline-override — nav item spacing must stay identical across all pages.
export const NAV_ITEM_GAP = "gap-7 lg:gap-9";

// Center-nav flat links. "Social" is rendered separately as a
// dropdown (see `SharedNavSocialMenu`) so this array intentionally
// omits it — the dropdown is appended after the flat links.
const CENTER_LINKS = [
  { label: "About", to: "/about" },
  { label: "Pricing", to: "/pricing" },
  { label: "Map", to: "/map" },
  { label: "FAQ", to: "/faq" },
];

/* ---------------------------------------------------------------------------
   Logo lockup — dog mark + typed wordmark. Sizes scale with viewport.

   App Shell Redesign Phase 1 — auth+view-aware target. Logged-in users
   land on their active workspace's dashboard, logged-out users land on
   the marketing surface. Resolution:
     - user === undefined (loading) → "/" (safe default; brief flash)
     - user === null (unauthed)     → "/"
     - user authed                  → pathForView(view) — admin /admin,
                                       provider /dashboard/provider, etc.
   --------------------------------------------------------------------------- */
function useLogoTarget() {
  const { user, view, availableViews } = useAuth();
  if (!user) return "/";
  // Prefer the active view; fall back to the highest-priority available
  // capability (matches the post-login default landing logic).
  const effectiveView = view || availableViews?.[0] || null;
  return effectiveView ? pathForView(effectiveView) : "/";
}

function LogoLockup({ size = "desktop" }) {
  const target = useLogoTarget();
  const { effective } = useTheme();
  const dogClass =
    size === "desktop" ? "h-12 w-auto" : "h-10 w-auto"; // 48px / 40px
  const wordClass =
    size === "desktop" ? "text-[28px]" : "text-[26px]"; // 28px / 26px
  // Phase L — swap to brand white asset on dark. Both files ship in
  // /public/brand/ from the brand-kit; never recreated per L spec.
  const dogSrc =
    effective === "dark"
      ? "/brand/final_white.png"
      : "/brand/poopluckers-dogmark.png";
  return (
    <Link
      to={target}
      className="inline-flex items-center gap-2.5 sm:gap-3"
      data-testid="shared-nav-logo-link"
      aria-label="PooPluckers home"
    >
      <img
        src={dogSrc}
        alt=""
        aria-hidden="true"
        className={`${dogClass} block pp-dog-mark`}
        data-testid="shared-nav-logo-mark"
      />
      <span
        className={`${wordClass} leading-none tracking-[-0.02em] select-none`}
        style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800 }}
        data-testid="shared-nav-logo-wordmark"
      >
        <span style={{ color: "var(--pp-orange)" }}>Poo</span>
        <span style={{ color: "var(--pp-slate)" }}>Pluckers</span>
      </span>
    </Link>
  );
}

/* ---------------------------------------------------------------------------
   Desktop center nav links cluster.
   --------------------------------------------------------------------------- */
function DesktopCenterLinks() {
  return (
    <nav
      aria-label="Site navigation"
      className={`flex items-center ${NAV_ITEM_GAP}`}
      data-testid="shared-nav-center-links"
    >
      {CENTER_LINKS.map(({ label, to }) => (
        <Link
          key={label}
          to={to}
          className="text-[15px] font-semibold transition-colors"
          style={{ color: "var(--pp-slate)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pp-orange-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pp-slate)")}
          data-testid={`shared-nav-center-${label.toLowerCase()}`}
        >
          {label}
        </Link>
      ))}
      {/* "Social" is a dropdown, not a flat link — its two child
          routes /social/just-plucking-around and /social/community
          are the real destinations. */}
      <SharedNavSocialMenu variant="desktop" />
    </nav>
  );
}

/* ---------------------------------------------------------------------------
   Brand rule (Phase D refresh — Item 3 extension, 2026-07-24).

   A single 9px solid orange band, full viewport width, no gaps. Pinned at
   the very top of every PUBLIC nav. Replaces the earlier 4-segment strip
   (charcoal / orange / charcoal / orange) which read as "broken" or
   "split" when it sat directly below the ContestPromoTopBar's purple
   band — the eye read the four quarters as a fractured rule rather than
   an intentional brand mark. Solid orange preserves the brand accent
   without the visual splintering.

   Color comes from the `--pp-orange` CSS custom property via the
   Tailwind `bg-pp-orange` utility (single source of truth — a theme
   edit to `--pp-orange` propagates here in lockstep with the buttons
   + logo lettering). No hardcoded hex literals.

   Only rendered on PUBLIC surfaces (when `rightSlot` is null). Auth
   app-shell headers (Admin / Customer / Provider — all of which pass
   a `rightSlot`) never see this decoration.
   --------------------------------------------------------------------------- */
function BrandSegmentStrip() {
  return (
    <div
      className="w-full bg-pp-orange"
      style={{ height: 9 }}
      aria-hidden="true"
      data-testid="shared-nav-brand-strip"
    />
  );
}

/* ---------------------------------------------------------------------------
   Default unauth right cluster — "Log in" rendered as brand-orange pill
   (Phase D). Uses the single-sourced `--pp-orange` + `--pp-slate` tokens
   so a theme edit updates the pill in lockstep with the rest of the app.
   The hover state matches the existing `.pp-btn-primary` pattern
   (`--pp-orange-hover` fill), so the pill's motion feels native to the
   codebase without introducing a new hover token.
   --------------------------------------------------------------------------- */
function DefaultRightCluster() {
  return (
    <Link
      to="/login"
      className="inline-flex items-center rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover px-5 py-2 text-[15px] font-bold transition-colors"
      data-testid="shared-nav-login-link"
    >
      Log in
    </Link>
  );
}

/* ---------------------------------------------------------------------------
   Mobile drawer contents (rendered inside the custom MobileNavDrawer).

   Layout (top → bottom):
     1. HEADER ROW — logo + close (✕). Logo uses the mobile variant
        (compact wordmark) so it never truncates at 320px. Close is
        44×44 tap area top-right, always fully in-viewport.
     2. PRIMARY CTAs — "Become a PooPartner" (filled orange, LEADS
        because partner-first launch posture) + "Join the waitlist"
        (outlined, secondary). Both full-width, min 44px tap height,
        both route to real shareable URLs (/partners → provider
        signup, /waitlist → real waitlist page).
     3. DIVIDER
     4. NAV LINKS — About / Pricing / Map / FAQ (min 44px tap
        height via py-3 vertical padding) + the Social dropdown
        (SharedNavSocialMenu variant="mobile", tap-to-expand).
     5. LOG IN — separate section, unauth only.
     6. FOOTER — theme toggle sits at the BOTTOM of the drawer
        (secondary setting, not header chrome). Uses a mt-auto
        pusher so it hugs the bottom regardless of nav length.
   --------------------------------------------------------------------------- */
function MobileDrawerBody({ rightSlotIsCustom, onClose }) {
  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid="shared-nav-mobile-drawer"
    >
      {/* 1. HEADER ROW — logo + close ONLY. Theme toggle removed
             from this row per mobile-drawer-fix Part 2. */}
      <header
        className="flex items-center justify-between gap-3 px-4 py-3 border-b border-pp-line"
        data-testid="shared-nav-mobile-drawer-header"
      >
        <LogoLockup size="mobile" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          data-testid="shared-nav-mobile-drawer-close"
          className="inline-flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange"
          style={{
            width: 44,
            height: 44,
            minWidth: 44,
            minHeight: 44,
            color: "var(--pp-slate)",
          }}
        >
          <X size={24} aria-hidden="true" />
        </button>
      </header>

      {/* Scrollable middle region so long nav content doesn't push
          the theme-toggle footer off-screen on shorter devices. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {/* 2. PRIMARY CTAs — partner-first order. Both use Family-L
               tokens. Primary "Become a PooPartner" is an ACTION button:
               `bg-pp-orange` + `text-pp-cta-text` (theme-invariant per
               Item 1 sweep 2026-07-23 — the old `text-pp-slate` flipped
               to near-white in dark mode, producing a WHITE-on-orange
               invisible button). The secondary "Join the waitlist" is
               a border-only style on the drawer's adaptive surface, so
               its `text-pp-slate` reads correctly in both themes.
               No inline hex. Both min 44px tap height via py-3
               (12px × 2 + 20px line-height = ~44px). */}
        <Link
          to="/partners"
          onClick={onClose}
          data-testid="shared-nav-mobile-cta-partner"
          className="block w-full rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover px-5 py-3 text-center text-[16px] font-bold transition-colors"
          style={{ minHeight: 44 }}
        >
          Become a PooPartner
        </Link>
        <Link
          to="/waitlist"
          onClick={onClose}
          data-testid="shared-nav-mobile-cta-waitlist"
          className="block w-full mt-3 rounded-full border-2 border-pp-slate text-pp-slate hover:border-pp-orange hover:text-pp-orange px-5 py-3 text-center text-[16px] font-bold transition-colors"
          style={{ minHeight: 44 }}
        >
          Join the waitlist
        </Link>

        {/* 3. DIVIDER */}
        <div
          className="my-6 w-full"
          style={{ height: 1, background: "var(--pp-line)" }}
          aria-hidden="true"
        />

        {/* 4. NAV LINKS — every link has py-3 → 44px tap height. */}
        <nav
          className="flex flex-col gap-0.5"
          aria-label="Site navigation"
          data-testid="shared-nav-mobile-links"
        >
          {CENTER_LINKS.map(({ label, to }) => (
            <Link
              key={label}
              to={to}
              onClick={onClose}
              className="block w-full text-[17px] font-semibold py-3 px-1 rounded-md transition-colors"
              style={{ minHeight: 44, color: "var(--pp-slate)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pp-orange-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pp-slate)")}
              data-testid={`shared-nav-mobile-link-${label.toLowerCase()}`}
            >
              {label}
            </Link>
          ))}
          {/* "Social" tap-to-expand dropdown for the mobile drawer.
              Same accessible contract as the desktop popover; the
              `mobile` variant renders inline instead of absolute. */}
          <SharedNavSocialMenu variant="mobile" onItemClick={onClose} />
        </nav>

        {/* 5. LOG IN — only on default (unauth) surfaces. */}
        {!rightSlotIsCustom && (
          <>
            <div
              className="w-full mt-6 mb-4"
              style={{ height: 1, background: "var(--pp-line)" }}
              aria-hidden="true"
            />
            <Link
              to="/login"
              onClick={onClose}
              className="block w-full text-center text-[16px] font-semibold py-3 rounded-md transition-colors"
              style={{ minHeight: 44, color: "var(--pp-slate)" }}
              data-testid="shared-nav-mobile-login-link"
            >
              Log in
            </Link>
          </>
        )}
      </div>

      {/* 6. FOOTER — theme toggle relocated here (secondary setting,
             below the divider and Log in link). Uses a hairline
             border so it visually separates from the scrollable
             region above. */}
      <footer
        className="border-t border-pp-line px-4 py-3 flex items-center justify-center"
        data-testid="shared-nav-mobile-drawer-footer"
      >
        <ThemeToggle />
      </footer>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Main component
   --------------------------------------------------------------------------- */
export default function SharedNav({ rightSlot = null }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const rightSlotIsCustom = rightSlot !== null && rightSlot !== undefined;
  // Phase D — Brand-book segment strip + 3px purple rule wrap ONLY the
  // public (unauth) nav. Auth surfaces pass a `rightSlot` (UserMenu /
  // avatar dropdown / role switcher) and are the sole discriminator.
  // Wrapping the decoration here (rather than at every caller) means a
  // future auth surface that adopts SharedNav automatically inherits
  // the plain treatment — and every public surface (Landing,
  // /about, /faq, /pricing, /login, /signup, /map, /social, /partners,
  // ComingSoon) inherits the Phase D look for free.
  const publicChrome = !rightSlotIsCustom;

  return (
    <header
      className="w-full"
      style={{ background: "var(--pp-v3-surface)" }}
      data-testid="shared-nav"
    >
      {publicChrome && <BrandSegmentStrip />}
      {/* Single-row layout: logo (left), center links (centered on desktop),
          right slot (Log in or injected UserMenu). On mobile, only logo +
          hamburger render. */}
      <div className="pp-wrap flex items-center justify-between gap-6 py-4">
        <LogoLockup size="desktop" />

        {/* Desktop center nav — hidden on mobile (drawer takes over). */}
        <div className="hidden md:flex flex-1 justify-center">
          <DesktopCenterLinks />
        </div>

        {/* Desktop right slot — always includes ThemeToggle (Phase L)
            sitting after the Log in pill / UserMenu. */}
        <div className="hidden md:flex items-center gap-2">
          {rightSlot || <DefaultRightCluster />}
          <ThemeToggle />
        </div>

        {/* Mobile hamburger (the only thing visible < md on the right side).
            On UserMenu surfaces (rightSlotIsCustom) we still show the
            hamburger — the drawer omits Log in automatically. */}
        <div className="md:hidden flex items-center gap-2">
          {/* If a custom rightSlot is in play (e.g. UserMenu avatar), show it
              even on mobile so authenticated users can still hit logout
              without opening the drawer. */}
          {rightSlotIsCustom && <div>{rightSlot}</div>}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md"
            style={{
              width: 44,
              height: 44,
              color: "var(--pp-slate)",
            }}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="shared-nav-mobile-drawer-panel"
            data-testid="shared-nav-hamburger-btn"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={24} aria-hidden />
          </button>
          <MobileNavDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            ariaLabel="Site navigation"
          >
            <MobileDrawerBody
              rightSlotIsCustom={rightSlotIsCustom}
              onClose={() => setDrawerOpen(false)}
            />
          </MobileNavDrawer>
        </div>
      </div>
      {publicChrome && (
        <div
          className="w-full bg-pp-purple"
          style={{ height: 3 }}
          aria-hidden="true"
          data-testid="shared-nav-purple-rule"
        />
      )}
    </header>
  );
}
